import { Component, inject, signal, OnInit, OnDestroy, output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Subject, takeUntil } from 'rxjs';
import { NotificationService } from '../../../core/services/notification.service';
import { AppNotification, NotificationType } from '../../../core/models/notification.models';
import { IconComponent } from '../ui/icon/icon.component';

@Component({
  selector: 'app-notification-panel',
  standalone: true,
  imports: [CommonModule, IconComponent],
  templateUrl: './notification-panel.component.html',
  styleUrl: './notification-panel.component.scss'
})
export class NotificationPanelComponent implements OnInit, OnDestroy {
  private notificationService = inject(NotificationService);
  private destroy$ = new Subject<void>();

  notifications = signal<AppNotification[]>([]);
  unreadCount = signal(0);
  isLoading = signal(false);

  close = output<void>();

  ngOnInit(): void {
    this.loadNotifications();
    
    this.notificationService.notifications$
      .pipe(takeUntil(this.destroy$))
      .subscribe(notifications => {
        this.notifications.set(notifications);
      });

    this.notificationService.unreadCount$
      .pipe(takeUntil(this.destroy$))
      .subscribe(count => {
        this.unreadCount.set(count);
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  loadNotifications(): void {
    this.isLoading.set(true);
    this.notificationService.loadNotifications().subscribe({
      next: () => this.isLoading.set(false),
      error: () => this.isLoading.set(false)
    });
  }

  markAsRead(notification: AppNotification): void {
    if (!notification.isRead) {
      this.notificationService.markAsRead(notification.id).subscribe();
    }
  }

  markAllAsRead(): void {
    this.notificationService.markAllAsRead().subscribe();
  }

  getNotificationIcon(type: NotificationType): string {
    switch (type) {
      case 'LEASE_EXPIRING_1_DAY':
      case 'LEASE_EXPIRING_3_DAYS':
        return 'Clock';
      case 'LEASE_EXPIRED':
        return 'AlertCircle';
      case 'WARNING':
        return 'AlertTriangle';
      case 'INFO':
      default:
        return 'Info';
    }
  }

  getNotificationIconClass(type: NotificationType): string {
    switch (type) {
      case 'LEASE_EXPIRING_1_DAY':
      case 'LEASE_EXPIRED':
        return 'icon-error';
      case 'LEASE_EXPIRING_3_DAYS':
      case 'WARNING':
        return 'icon-warning';
      case 'INFO':
      default:
        return 'icon-info';
    }
  }

  getTimeAgo(dateString: string): string {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Щойно';
    if (diffMins < 60) return `${diffMins} хв тому`;
    if (diffHours < 24) return `${diffHours} год тому`;
    if (diffDays < 7) return `${diffDays} дн тому`;
    
    return date.toLocaleDateString('uk-UA', { day: 'numeric', month: 'short' });
  }

  onClose(): void {
    this.close.emit();
  }

  onBackdropClick(event: MouseEvent): void {
    if ((event.target as HTMLElement).classList.contains('notification-backdrop')) {
      this.onClose();
    }
  }
}
